import type { TFunction } from 'i18next';

/**
 * How a validation finding's message becomes user-facing text on the client
 * (R8.9, R18.9, R18.16).
 *
 * A finding never carries user-facing text. It carries a `messageKey` — an i18n
 * key that resolves against the same catalogue every other label comes from — and,
 * for a configurable rule, an optional `messageOverride` an administrator set
 * (R8.9). Resolution is therefore a two-step rule with no text ever inlined at
 * the call site:
 *
 * 1. If the finding has a non-empty `messageOverride`, that configured text wins.
 * 2. Otherwise the `messageKey` resolves through the catalogue.
 *
 * `detail` values (e.g. a field name, a version, a timestamp) are passed to
 * i18next as interpolation variables, so a message key with `{{...}}` placeholders
 * fills from the finding's own detail.
 */
export interface ResolvableFinding {
  /** The i18n key the domain/API emitted; resolves through the catalogue (R18.9). */
  readonly messageKey: string;
  /** Administrator-configured text for a configurable rule, or null/undefined (R8.9). */
  readonly messageOverride?: string | null;
  /** Structured detail passed to the message key as interpolation variables. */
  readonly detail?: Readonly<Record<string, unknown>> | undefined;
}

/**
 * Resolves one finding to display text: the configured override if present, else
 * the catalogue text for its `messageKey`, with the finding's `detail` supplied as
 * interpolation variables.
 */
export function resolveFindingMessage(t: TFunction, finding: ResolvableFinding): string {
  const override = finding.messageOverride;
  if (typeof override === 'string' && override.length > 0) {
    return override;
  }
  return t(finding.messageKey, { ...(finding.detail ?? {}) });
}
